import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Divider
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore'
import toast from 'react-hot-toast'
import MiniQuizSolve from './MiniQuizSolve'
import WordTaskSolve from './WordTaskSolve'

const StudentTasks = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [completionStatus, setCompletionStatus] = useState({})
  const [currentView, setCurrentView] = useState('list') // 'list', 'quiz-solve', 'word-task-solve'
  const [selectedTask, setSelectedTask] = useState(null)

  useEffect(() => {
    if (user && user.uid) {
      loadTasks()
    }
  }, [user])

  const loadTasks = async () => {
    try {
      setLoading(true)
      
      // Android uygulamasıyla aynı koleksyonları kullan: miniQuizzes ve wordTasks
      
      // Mini Quiz görevlerini yükle (Android uygulamasıyla aynı)
      const miniQuizQuery = query(
        collection(db, 'miniQuizzes'),
        where('studentIds', 'array-contains', user.uid)
      )
      const miniQuizSnapshot = await getDocs(miniQuizQuery)
      
      // Kelime görevlerini yükle (Android uygulamasıyla aynı)
      const wordTaskQuery = query(
        collection(db, 'wordTasks'),
        where('studentIds', 'array-contains', user.uid)
      )
      const wordTaskSnapshot = await getDocs(wordTaskQuery)
      
      const allTasks = []
      
      // Mini Quiz görevlerini ekle (Android uygulamasındaki alanlarla)
      miniQuizSnapshot.docs.forEach(doc => {
        const data = doc.data()
        allTasks.push({
          id: doc.id,
          title: data.title || 'Başlıksız Quiz',
          type: 'quiz',
          description: data.description || `Mini Quiz`,
          createdDate: data.createdDate, // Android'deki alan adı
          dueDate: data.dueDate, // Son tarih
          unlimited: data.unlimited || false, // Sınırsız mı
          timeLimit: data.timeLimit || 0, // Süre sınırı
          teacherName: data.teacherName || 'Öğretmen',
          className: data.className || '',
          allowMultipleAttempts: data.allowMultipleAttempts !== false, // Varsayılan true
          taskType: 'quiz',
          completed: false // Sonuçlardan kontrol edilecek
        })
      })
      
      // Kelime görevlerini ekle (Android uygulamasındaki alanlarla)
      wordTaskSnapshot.docs.forEach(doc => {
        const data = doc.data()
        const wordCount = data.words ? data.words.length : 0
        allTasks.push({
          id: doc.id,
          title: data.title || 'Başlıksız Kelime Görevi',
          type: 'wordTask',
          description: data.description || `${wordCount} kelime`,
          createdDate: data.createdDate, // Android'deki alan adı
          dueDate: data.dueDate, // Son tarih
          unlimited: data.unlimited || false, // Sınırsız mı
          teacherName: data.teacherName || 'Öğretmen',
          className: data.className || '',
          taskMode: data.taskMode || 'meaning', // Görev modu
          wordCount: wordCount,
          allowMultipleAttempts: true, // Kelime görevleri için her zaman true
          taskType: 'word',
          completed: false // Sonuçlardan kontrol edilecek
        })
      })
      
      // Tarihe göre sırala (createdDate kullan, Android uygulamasıyla aynı)
      allTasks.sort((a, b) => {
        if (a.createdDate && b.createdDate) {
          return b.createdDate.toDate() - a.createdDate.toDate()
        }
        return 0
      })
      
      console.log('📋 Yüklenen görevler:', allTasks)
      setTasks(allTasks)
      
      // Tamamlanma durumlarını kontrol et
      await checkCompletionStatus(allTasks)
    } catch (error) {
      console.error('Error loading tasks:', error)
      toast.error('Görevler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const checkCompletionStatus = async (taskList) => {
    const statusMap = {}
    
    for (const task of taskList) {
      try {
        if (task.type === 'quiz') {
          // Quiz tamamlanma durumunu kontrol et (Android uygulamasıyla aynı)
          const quizResultQuery = query(
            collection(db, 'quizResults'),
            where('quizId', '==', task.id),
            where('studentId', '==', user.uid)
          )
          const quizResultSnapshot = await getDocs(quizResultQuery)
          
          if (!quizResultSnapshot.empty) {
            const resultDoc = quizResultSnapshot.docs[0]
            const resultData = resultDoc.data()
            statusMap[task.id] = {
              completed: true,
              score: resultData.score || 0,
              totalQuestions: resultData.totalQuestions || 0,
              completionDate: resultData.completionDate
            }
          } else {
            statusMap[task.id] = { completed: false }
          }
        } else if (task.type === 'wordTask') {
          // Kelime görevi tamamlanma durumunu kontrol et (Android uygulamasıyla aynı)
          const wordTaskResultQuery = query(
            collection(db, 'wordTaskResults'),
            where('wordTaskId', '==', task.id),
            where('studentId', '==', user.uid)
          )
          const wordTaskResultSnapshot = await getDocs(wordTaskResultQuery)
          
          if (!wordTaskResultSnapshot.empty) {
            const resultDoc = wordTaskResultSnapshot.docs[0]
            const resultData = resultDoc.data()
            statusMap[task.id] = {
              completed: true,
              score: resultData.score || 0,
              totalQuestions: resultData.totalQuestions || 0,
              completionDate: resultData.completionDate
            }
          } else {
            statusMap[task.id] = { completed: false }
          }
        }
      } catch (error) {
        console.error(`Error checking completion for task ${task.id}:`, error)
        statusMap[task.id] = { completed: false }
      }
    }
    
    setCompletionStatus(statusMap)
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Tarih belirtilmemiş'
    return timestamp.toDate().toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTaskIcon = (type) => {
    switch (type) {
      case 'quiz':
        return <AssignmentIcon color="primary" />
      case 'wordTask':
        return <StarIcon color="warning" />
      default:
        return <AssignmentIcon />
    }
  }

  const getTaskTypeLabel = (type) => {
    switch (type) {
      case 'quiz':
        return 'Mini Quiz'
      case 'wordTask':
        return 'Kelime Görevi'
      default:
        return 'Görev'
    }
  }

  const handleTaskStart = (task) => {
    // Son tarih kontrolü (Android uygulamasıyla aynı)
    if (task.dueDate && !task.unlimited) {
      const currentDate = new Date()
      const dueDate = task.dueDate.toDate()
      if (currentDate > dueDate && !completionStatus[task.id]?.completed) {
        toast.error('Bu görevin süresi dolmuş!')
        return
      }
    }

    setSelectedTask(task)

    if (task.type === 'quiz') {
      // Mini quiz'i başlat (Android uygulamasıyla aynı)
      setCurrentView('quiz-solve')
    } else if (task.type === 'wordTask') {
      // Kelime görevini aç (Android uygulamasıyla aynı)
      setCurrentView('word-task-solve')
    }
  }

  const handleBackToTaskList = () => {
    setCurrentView('list')
    setSelectedTask(null)
    // Görevler listesini yenile
    loadTasks()
  }

  const getTaskStatus = (task) => {
    const status = completionStatus[task.id]
    if (!status) return { text: 'Yükleniyor...', color: 'text.secondary' }
    
    if (status.completed) {
      return { 
        text: 'Tamamlandı', 
        color: 'success.main',
        score: `${status.score}/${status.totalQuestions}`
      }
    } else {
      return { text: 'Bekliyor', color: 'warning.main' }
    }
  }

  const getTaskButton = (task) => {
    const status = completionStatus[task.id]
    if (!status) return { text: 'Yükleniyor...', disabled: true, variant: 'outlined' }
    
    if (status.completed) {
      if (task.allowMultipleAttempts) {
        return { 
          text: 'Tekrar Çöz', 
          disabled: false, 
          variant: 'outlined',
          color: 'secondary'
        }
      } else {
        return { 
          text: 'Çözüldü', 
          disabled: true, 
          variant: 'outlined',
          color: 'default'
        }
      }
    } else {
      return { 
        text: 'Başla', 
        disabled: false, 
        variant: 'contained',
        color: 'primary'
      }
    }
  }

  // Quiz çözme sayfasını göster
  if (currentView === 'quiz-solve' && selectedTask) {
    return (
      <MiniQuizSolve
        quizId={selectedTask.id}
        quizTitle={selectedTask.title}
        quizDescription={selectedTask.description}
        timeLimit={selectedTask.timeLimit}
        onBack={handleBackToTaskList}
      />
    )
  }

  // Kelime görevi çözme sayfasını göster
  if (currentView === 'word-task-solve' && selectedTask) {
    return (
      <WordTaskSolve
        taskId={selectedTask.id}
        taskTitle={selectedTask.title}
        taskDescription={selectedTask.description}
        taskMode={selectedTask.taskMode}
        onBack={handleBackToTaskList}
      />
    )
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Görevlerim
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
          {/* Header Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                📋 Atanan Görevler
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Öğretmenleriniz tarafından size atanan quiz ve kelime görevlerini burada görebilirsiniz.
              </Typography>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Görevler yükleniyor...</Typography>
            </Box>
          ) : tasks.length === 0 ? (
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Henüz görev bulunmuyor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Öğretmenleriniz size görev atadığında burada görünecektir.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Görev Listesi ({tasks.length})
                </Typography>
                
                <List>
                  {tasks.map((task, index) => (
                    <React.Fragment key={task.id}>
                                              <ListItem
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                          }
                        }}
                      >
                        <ListItemIcon>
                          {getTaskIcon(task.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  {task.title}
                                </Typography>
                                <Chip 
                                  label={getTaskTypeLabel(task.type)} 
                                  size="small" 
                                  color={task.type === 'quiz' ? 'primary' : 'warning'}
                                />
                                <Chip 
                                  label={getTaskStatus(task).text}
                                  size="small" 
                                  sx={{ color: getTaskStatus(task).color }}
                                  variant="outlined"
                                />
                                {getTaskStatus(task).score && (
                                  <Chip 
                                    label={`Puan: ${getTaskStatus(task).score}`}
                                    size="small" 
                                    color="success"
                                  />
                                )}
                              </Box>
                              <Button
                                {...getTaskButton(task)}
                                onClick={() => handleTaskStart(task)}
                                sx={{ ml: 2 }}
                              >
                                {getTaskButton(task).text}
                              </Button>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {task.description} • {task.teacherName}
                                {task.className && ` • ${task.className}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <ScheduleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                Oluşturuldu: {formatDate(task.createdDate)}
                              </Typography>
                              {task.dueDate && !task.unlimited && (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                                  ⏰ Son Tarih: {formatDate(task.dueDate)}
                                </Typography>
                              )}
                              {task.unlimited && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                  ♾️ Sınırsız Süre
                                </Typography>
                              )}
                              {task.timeLimit > 0 && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                  ⏱️ Süre Sınırı: {task.timeLimit} dakika
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < tasks.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              💡 <strong>Bilgi:</strong> Görevleri tamamlamak için Education panelindeki ilgili bölümleri ziyaret edebilirsiniz.
            </Typography>
          </Alert>
        </Container>
      </Box>
    </SpaceTheme>
  )
}

export default StudentTasks
